/**
 * "2nd ticket parked" — the amber badge on a job (OPEN-144 PR 3, §2b). For a reviewer it is
 * a link: to the ticket in In Review while the job is In Review, otherwise to the Sync page
 * filtered to this job (the read-only compare and its Keep). Everyone else sees the badge
 * only (a dispatcher has no REVIEW_TICKET).
 */

import React from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import { REVIEW_ROLES } from '../../lib/role-gates';
import { formatNumber } from '@lodex-pro/shared/number-format';

const STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 600,
  backgroundColor: 'rgba(246, 182, 94,0.18)',
  color: 'var(--color-warning)',
  border: 'none',
};

export function parkedBadgeTarget(jobId: string, statusCode: string): string {
  return statusCode === 'IN_REVIEW'
    ? `/?tab=review&jobId=${encodeURIComponent(jobId)}`
    : `/sync?jobId=${encodeURIComponent(jobId)}`;
}

function label(count: number): string {
  return count === 1 ? '2nd ticket parked' : `${formatNumber(count)} tickets parked`;
}

function BadgeLink({
  jobId,
  statusCode,
  count,
  onNavigate,
}: {
  jobId: string;
  statusCode: string;
  count: number;
  onNavigate?: () => void;
}): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      style={{ ...STYLE, cursor: 'pointer' }}
      title={statusCode === 'IN_REVIEW' ? 'Open in In Review' : 'Open on the Sync page'}
      onClick={(): void => {
        onNavigate?.();
        void navigate(parkedBadgeTarget(jobId, statusCode));
      }}
    >
      {label(count)}
    </button>
  );
}

export function ParkedBadge({
  jobId,
  statusCode,
  count,
  role,
  onNavigate,
}: {
  jobId: string;
  statusCode: string;
  count: number | null | undefined;
  role: string;
  /** e.g. close the modal before leaving. */
  onNavigate?: () => void;
}): React.JSX.Element | null {
  const inRouter = useInRouterContext();
  if (count == null || count <= 0) return null;
  if (inRouter && REVIEW_ROLES.has(role)) {
    return (
      <BadgeLink
        jobId={jobId}
        statusCode={statusCode}
        count={count}
        {...(onNavigate !== undefined && { onNavigate })}
      />
    );
  }
  return (
    <span style={STYLE} data-testid="parked-badge">
      {label(count)}
    </span>
  );
}
